
import Button from 'components/Button';
import Form, { useForm } from 'components/Form';
import Modal from 'components/Modal';
import Text from 'components/Text';
import cashierActions from 'features/cashier/services/actions';
import { ICreateCustomerBody } from 'features/cashier/services/types/api';
import { ICustomerFormValues } from 'features/cashier/services/types/customer';
import { forwardRef, useImperativeHandle, useRef, useState } from 'react';
import { useAppDispatch } from 'store/hooks';
import { PHONE_MASKED } from 'utils/mask';
import FormAddCustomer from './Form';
import Icon from 'assets/Icons';
import { InputRef, Row } from 'antd';
import styled from 'styled-components';

type Props = {
  model?: 'default' | 'new-ticket';
  short?: boolean;
  v2?: boolean;
};
type IButtonAddCustomerRef = {
  open: ({ phone, name }: { phone?: string, name?: string }) => void;
};

export const useButtonAddCustomerRef = () => useRef<IButtonAddCustomerRef>(null);

const ButtonAddCustomer = forwardRef<IButtonAddCustomerRef, Props>(({ model = 'default', short = false, v2 }, ref) => {
  const inputRef = useRef<InputRef>(null);
  const [visible, setVisible] = useState(false);
  const [form] = useForm();
  const dispatch = useAppDispatch();
  const onOpen = () => {
    setTimeout(() => inputRef.current?.focus(), 10);
    setVisible(true);
  };

  const onClose = () => {
    form.resetFields();
    setVisible(false);
  };
  const valid = async () => {
    try {
      await form.validateFields();
      return true;
    } catch (error) {
      return false;
    }
  };
  const onSubmit = async () => {
    if (!await valid()) return;
    form.submit();
  };

  const handleFinish = async (values: ICustomerFormValues) => {
    onClose();
    PHONE_MASKED.resolve(values?.phone);
    const phone = PHONE_MASKED.unmaskedValue;
    const payload: ICreateCustomerBody = {
      phone: phone || null,
      name: values.name || null,
      email: values.email || null,
      birthday: values.birthday || null,
      phoneReferral: values.phoneReferral?.replace(/[^0-9]/g, '') || null,
    };
    dispatch(cashierActions.addNewCustomer.fetch(payload));
  };

  useImperativeHandle(ref, () => ({
    open({ phone, name }) {
      form.setFieldsValue({
        name,
        phone,
      });
      setVisible(true);
    },
  }));

  const Btn = () => {
    if (v2) {
      return (
        <ButtonAddNewV2 onClick={onOpen}>
          <AddIcon />
          <span>New Customer</span>
        </ButtonAddNewV2>
      );
    }
    if (model === 'new-ticket') {
      return (
        <Button
          onClick={onOpen}
          icon='addPlus'
          ntype='PRIMARY'
        />
      );
    }
    return (
      <button onClick={onOpen}>
        <Row align={'middle'}>
          <Icon type='addNewPlus' />
          <Text color="info_3" variant="CONTENT_2">
            {short ? 'New Customer' : 'Add New Customer'}
          </Text>
        </Row>
      </button>
    );
  };

  return (
    <>
      <Btn />

      <Modal
        v2={v2}
        visible={visible}
        modalTitle='ADD NEW CUSTOMER'
        onClose={onClose}
        onSubmit={onSubmit}
        okTitle='Done'
        containerPadding={v2 ? 0 : undefined}
        noneBodyStyle={v2}
        width={v2 ? '880px' : undefined}
      >
        <FormStyled
          form={form}
          className={v2 ? 'v2' : ''}
          onFinish={handleFinish}
        >
          <FormAddCustomer inputRef={inputRef} />
        </FormStyled>
      </Modal>
    </>
  );
});
ButtonAddCustomer.displayName = 'ButtonAddCustomerCashier';
export default ButtonAddCustomer;
const FormStyled = styled(Form)`
&.v2 .FormAddCustomerStyled {
padding: 16px;
padding-bottom: 0;
 .ant-form-item .ant-form-item-label label {
  color: #1D2129;
  font-family: Poppins;
  font-size: 20px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
 } 

 .ant-form-item .ant-form-item-control-input input {
    color: #1D2129;
    font-family: Poppins;
    font-size: 20px;
    font-style: normal;
    font-weight: 500;
    line-height: normal;
 }

 .InputEmailStyled .suggestion {
  .box-item {
    flex: 1;
    display: flex;
    padding: 8px 24px;
    justify-content: center;
    align-items: center;
    gap: 4px;
    flex: 1 0 0;
    border-radius: 100px;
    border: 1px solid #4384FF;
    background: #FFF;
    .CONTENT_1 {
      color: #4384FF;
      text-align: center;
      font-family: Poppins;
      font-size: 20px;
      font-style: normal;
      font-weight: 600;
      line-height: 22px;
    }
  }
 }
}
`;

const ButtonAddNewV2 = styled.button`
display: flex;
padding: 16px 24px;
flex-direction: column;
justify-content: center;
align-items: center;
gap: 4px;
flex: 1 0 0;
align-self: stretch;
border-radius: 4px;
background: #C6E9EF;
box-shadow: 0px 2px 4px 0px rgba(44, 35, 66, 0.40), 0px 4px 8px -3px rgba(45, 35, 66, 0.30), 0px -3px 0px 0px #D6D6E7 inset;
span {
  color: #1D2129;
  text-align: center;
  font-family: Poppins;
  font-size: 18px;
  font-style: normal;
  font-weight: 600;
  line-height: normal;
}
`;
const AddIcon = () => (
  <svg width="48" height="48" viewBox="0 0 48 48" fill="none" xmlns="http://www.w3.org/2000/svg">
    <circle cx="24" cy="10" r="8" fill="#1D2129" />
    <path d="M8 44H40L39.1111 31.3684L35.5556 26.1053L25.7778 24L16 25.0526L8.88889 32.4211L8 44Z" fill="#1D2129" />
    <mask id="path-3-inside-1_11371_818" fill="white">
      <path d="M24 21C22.0222 21 20.0888 20.4135 18.4443 19.3147C16.7998 18.2159 15.5181 16.6541 14.7612 14.8268C14.0043 12.9996 13.8063 10.9889 14.1922 9.0491C14.578 7.10929 15.5304 5.32746 16.9289 3.92894C18.3275 2.53041 20.1093 1.578 22.0491 1.19215C23.9889 0.8063 25.9996 1.00433 27.8268 1.76121C29.6541 2.51809 31.2159 3.79981 32.3147 5.4443C33.4135 7.08879 34 9.02219 34 11C34 13.6522 32.9464 16.1957 31.0711 18.0711C29.1957 19.9464 26.6522 21 24 21ZM24 5C22.8133 5 21.6533 5.3519 20.6666 6.01119C19.6799 6.67047 18.9109 7.60755 18.4567 8.7039C18.0026 9.80026 17.8838 11.0067 18.1153 12.1705C18.3468 13.3344 18.9182 14.4035 19.7574 15.2426C20.5965 16.0818 21.6656 16.6532 22.8295 16.8847C23.9933 17.1162 25.1997 16.9974 26.2961 16.5433C27.3925 16.0892 28.3295 15.3201 28.9888 14.3334C29.6481 13.3467 30 12.1867 30 11C30 9.4087 29.3679 7.88258 28.2426 6.75736C27.1174 5.63214 25.5913 5 24 5Z" />
    </mask>
    <path d="M24 21C22.0222 21 20.0888 20.4135 18.4443 19.3147C16.7998 18.2159 15.5181 16.6541 14.7612 14.8268C14.0043 12.9996 13.8063 10.9889 14.1922 9.0491C14.578 7.10929 15.5304 5.32746 16.9289 3.92894C18.3275 2.53041 20.1093 1.578 22.0491 1.19215C23.9889 0.8063 25.9996 1.00433 27.8268 1.76121C29.6541 2.51809 31.2159 3.79981 32.3147 5.4443C33.4135 7.08879 34 9.02219 34 11C34 13.6522 32.9464 16.1957 31.0711 18.0711C29.1957 19.9464 26.6522 21 24 21ZM24 5C22.8133 5 21.6533 5.3519 20.6666 6.01119C19.6799 6.67047 18.9109 7.60755 18.4567 8.7039C18.0026 9.80026 17.8838 11.0067 18.1153 12.1705C18.3468 13.3344 18.9182 14.4035 19.7574 15.2426C20.5965 16.0818 21.6656 16.6532 22.8295 16.8847C23.9933 17.1162 25.1997 16.9974 26.2961 16.5433C27.3925 16.0892 28.3295 15.3201 28.9888 14.3334C29.6481 13.3467 30 12.1867 30 11C30 9.4087 29.3679 7.88258 28.2426 6.75736C27.1174 5.63214 25.5913 5 24 5Z" fill="#1D2129" />
    <path d="M24 21L24 4.33334L24 21ZM34 11L17.3333 11L34 11ZM30 11L46.6667 11L30 11ZM24 4.33334C25.3185 4.33334 26.6075 4.72433 27.7038 5.45687L9.1848 33.1725C13.5701 36.1027 18.7258 37.6667 24 37.6667L24 4.33334ZM27.7038 5.45687C28.8001 6.18941 29.6546 7.2306 30.1592 8.44878L-0.636783 21.2049C1.38156 26.0776 4.79949 30.2424 9.1848 33.1725L27.7038 5.45687ZM30.1592 8.44878C30.6638 9.66696 30.7958 11.0074 30.5386 12.3006L-2.15427 5.7976C-3.18321 10.9704 -2.65512 16.3322 -0.636783 21.2049L30.1592 8.44878ZM30.5386 12.3006C30.2813 13.5938 29.6464 14.7817 28.714 15.714L5.14382 -7.85618C1.41442 -4.12677 -1.12533 0.624771 -2.15427 5.7976L30.5386 12.3006ZM28.714 15.714C27.7817 16.6464 26.5938 17.2813 25.3006 17.5386L18.7976 -15.1543C13.6248 -14.1253 8.87322 -11.5856 5.14382 -7.85618L28.714 15.714ZM25.3006 17.5386C24.0074 17.7958 22.667 17.6638 21.4488 17.1592L34.2049 -13.6368C29.3322 -15.6551 23.9704 -16.1832 18.7976 -15.1543L25.3006 17.5386ZM21.4488 17.1592C20.2306 16.6546 19.1894 15.8001 18.4569 14.7038L46.1725 -3.8152C43.2424 -8.20052 39.0776 -11.6184 34.2049 -13.6368L21.4488 17.1592ZM18.4569 14.7038C17.7243 13.6075 17.3333 12.3185 17.3333 11H50.6667C50.6667 5.72584 49.1027 0.570115 46.1725 -3.8152L18.4569 14.7038ZM17.3333 11C17.3333 9.23189 18.0357 7.5362 19.286 6.28596L42.8562 29.8562C47.8571 24.8552 50.6667 18.0724 50.6667 11L17.3333 11ZM19.286 6.28596C20.5362 5.03572 22.2319 4.33334 24 4.33334L24 37.6667C31.0724 37.6667 37.8552 34.8572 42.8562 29.8562L19.286 6.28596ZM24 -11.6667C19.517 -11.6667 15.1346 -10.3373 11.4071 -7.84664L29.9261 19.869C28.172 21.0411 26.1097 21.6667 24 21.6667V-11.6667ZM11.4071 -7.84664C7.67956 -5.356 4.77432 -1.81595 3.05873 2.32585L33.8547 15.082C33.0474 17.031 31.6802 18.6969 29.9261 19.869L11.4071 -7.84664ZM3.05873 2.32585C1.34315 6.46764 0.894272 11.0251 1.76887 15.4221L34.4617 8.91904C34.8733 10.9882 34.6621 13.1329 33.8547 15.082L3.05873 2.32585ZM1.76887 15.4221C2.64347 19.819 4.80226 23.8578 7.97225 27.0278L31.5425 3.45753C33.0342 4.94929 34.0501 6.84991 34.4617 8.91904L1.76887 15.4221ZM7.97225 27.0278C11.1422 30.1977 15.1811 32.3565 19.578 33.2311L26.081 0.538294C28.1501 0.94987 30.0507 1.96577 31.5425 3.45753L7.97225 27.0278ZM19.578 33.2311C23.9749 34.1057 28.5324 33.6569 32.6742 31.9413L19.918 1.14529C21.8671 0.337954 24.0118 0.126719 26.081 0.538294L19.578 33.2311ZM32.6742 31.9413C36.816 30.2257 40.356 27.3204 42.8466 23.5929L15.131 5.07392C16.3031 3.3198 17.969 1.95263 19.918 1.14529L32.6742 31.9413ZM42.8466 23.5929C45.3373 19.8654 46.6667 15.483 46.6667 11L13.3333 11C13.3333 8.89034 13.9589 6.82805 15.131 5.07392L42.8466 23.5929ZM46.6667 11C46.6667 4.98844 44.2786 -0.776915 40.0278 -5.02775L16.4575 18.5425C14.4571 16.5421 13.3333 13.829 13.3333 11L46.6667 11ZM40.0278 -5.02775C35.7769 -9.27857 30.0116 -11.6667 24 -11.6667L24 21.6667C21.171 21.6667 18.4579 20.5429 16.4575 18.5425L40.0278 -5.02775Z" fill="#1D2129" mask="url(#path-3-inside-1_11371_818)" />
    <path d="M40.2 46H7.8C7.32261 46 6.86477 45.7893 6.52721 45.4142C6.18964 45.0391 6 44.5304 6 44V38C6 33.7565 7.51714 29.6869 10.2177 26.6863C12.9182 23.6857 16.5809 22 20.4 22H27.6C31.4191 22 35.0818 23.6857 37.7823 26.6863C40.4829 29.6869 42 33.7565 42 38V44C42 44.5304 41.8104 45.0391 41.4728 45.4142C41.1352 45.7893 40.6774 46 40.2 46ZM9.6 42H38.4V38C38.4 34.8174 37.2621 31.7652 35.2368 29.5147C33.2114 27.2643 30.4643 26 27.6 26H20.4C17.5357 26 14.7886 27.2643 12.7632 29.5147C10.7379 31.7652 9.6 34.8174 9.6 38V42Z" fill="#1D2129" />
  </svg>
);
